<template>
  <div class="flex items-center relative">
    <span class="absolute pointer-events-none left-2 text-sm text-gray-600">
      {{ currencyPrefix }}
    </span>
    <input
      ref="inputRef"
      class="input"
      :style="{
        paddingLeft: `${currencyPrefix.length === 4 ? '2.75' : '2.1'}rem`,
      }"
      inputmode="decimal"
      :disabled="disabled"
      :data-disabled="disabled"
      type="text"
      placeholder="0.00"
      @input="handleInput"
      @blur="handleBlur"
    >
  </div>
</template>
<script lang="ts">
import { getCurrencyPrefix } from '@/filters/currency';
import {
  defineComponent,
  ref,
  computed,
  watch,
} from 'vue';

export function maskInputValue(inputValue: string) : string {
  const pruned = inputValue.replace(/[^\d.,]/g, '');
  const split = pruned.split('.');
  const tooManyDecimals = split.length > 2;
  const tooMuchPrecision = split[1] && split[1].length > 2;
  if (tooManyDecimals || tooMuchPrecision) {
    return `${split[0]}.${split[1].slice(0, 2)}`;
  }
  return pruned;
}

/**
 * Converts any string amount that might be in the input into cents
 * Removes extra characters, calculates dollars and cents based on if the "." is there
 */
function stringAmountToCents(amount: string) : number {
  // This removes that aren't digits or periods. Note that this is not the same as maskInputValue
  // because that lets you type in commas, but this strips those out
  const pruned = amount.replace(/[^\d.]/g, '');

  const split = pruned.split('.');
  if (split.length > 2) {
    throw new Error('Invalid input value: too many full stops');
  }
  let amountInCents = 0;
  const dollars = split[0];
  const cents = split[1];

  if (dollars) {
    amountInCents += parseInt(dollars, 10) * 100;
  }
  if (cents) {
    if (cents.length === 1) {
      amountInCents += parseInt(cents, 10) * 10;
    } else {
      amountInCents += parseInt(cents, 10);
    }
  }

  return amountInCents;
}

/**
 * Converts cents to a string to be displayed in the input
 */
export function centsToInputValue(amountInCents: number) : string {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInCents / 100);
}

export default defineComponent({
  props: {
    amountInCents: {
      type: Number,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  emits: ['change:amountInCents'],
  setup(props, { emit }) {
    const emitDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = ref<HTMLInputElement | null>(null);

    const currencyPrefix = computed<string>((): string => {
      return getCurrencyPrefix(props.currency);
    });

    type EventResult = {
      type: 'invalid' | 'no_change' | 'changed';
      cents: number | undefined;
      stringValue: string;
    };
    const processEvent = (event: Event) : EventResult => {
      const target = event.target as HTMLInputElement;
      const newInputValue = target.value;

      // This stops the user from typing invalid characters, like "90d"
      const maskedInputValue = maskInputValue(newInputValue);
      if (maskedInputValue !== newInputValue) {
        target.value = maskedInputValue;
        return {
          type: 'invalid',
          cents: undefined,
          stringValue: maskedInputValue,
        };
      }

      const newCents = stringAmountToCents(maskedInputValue);
      const oldCents = props.amountInCents;
      if (newCents === oldCents) {
        return {
          type: 'no_change',
          cents: oldCents,
          stringValue: maskedInputValue,
        };
      }
      return {
        type: 'changed',
        cents: newCents,
        stringValue: maskedInputValue,
      };
    };

    /**
     * This handles all the typing and pasting that can happen in the input
     */
    const handleInput = (event: Event) => {
      const result = processEvent(event);
      if (result.type === 'changed') {
        if (emitDebounceTimeout.value !== null) {
          clearTimeout(emitDebounceTimeout.value);
        }
        // This check is here so that if it says "5,000.00" and you want to channge it to 4k,
        // it won't emit an event when you delete the 4 and are left with ",000.00"
        if (result.stringValue.startsWith('.') || result.stringValue.startsWith(',')) {
          return;
        }
        // This is for when you change "$200.00" by deleting the 2 and typing a 1. We do NOT
        // want to emit the value of "0" while you're still typing
        if (result.cents === 0 && result.stringValue.length >= 4) {
          return;
        }
        emitDebounceTimeout.value = setTimeout(emitAmountInCents, 500);
      }
    };

    const handleBlur = (event: Event) => {
      const result = processEvent(event);
      if (result.type === 'changed') {
        emitAmountInCents();
      }
      inputRef.value!.value = centsToInputValue(result.cents ?? 0);
    };

    const emitAmountInCents = () => {
      if (emitDebounceTimeout.value !== null) {
        clearTimeout(emitDebounceTimeout.value);
      }
      if (!inputRef.value) {
        return;
      }
      const newCents = stringAmountToCents(inputRef.value.value);
      emit('change:amountInCents', newCents);
    };

    function updateInputValueFromProps() {
      if (!inputRef.value) {
        return;
      }
      if (document.activeElement === inputRef.value) {
        const propsCents = props.amountInCents;
        const inputCents = stringAmountToCents(inputRef.value.value);
        if (propsCents === inputCents) {
          return;
        }
      }
      inputRef.value.value = centsToInputValue(props.amountInCents);
    }

    watch(() => props.amountInCents, updateInputValueFromProps);
    watch(() => inputRef.value, () => {
      updateInputValueFromProps();
    });

    return {
      currencyPrefix,
      handleInput,
      handleBlur,
      inputRef,
    };
  },
});
</script>

<style lang="scss" scoped>
.input {
  @apply py-1 text-sm border border-gray-300 placeholder-gray-400 rounded;
  @apply focus:ring-2 focus:ring-primary-500 focus:border-transparent max-w-32 min-w-20;

  &:disabled {
    @apply bg-gray-200 text-gray-500 border-none shadow-[0_2px_8px_0_rgba(44,62,80,0.06)_inset];
  }
}
</style>
